import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
// NOTE: adjust these two import paths if they differ in the actual project —
// the rest of the codebase already uses `HttpService` + `URLUtils` (see the
// TODO comments below, which mirror the existing calling convention used
// throughout this file before this change).
import { HttpService } from './http.service';
import { URLUtils } from '../urlUtils';

export interface SupportingDocument {
    id: string;
    name: string;
    fileType: string;    // derived from the file extension, e.g. 'PDF', 'ZIP'
    sizeLabel?: string;  // not provided by the real API; template doesn't render it
    uploadedOn?: string; // e.g. 'Aug 14, 2026' — shown on the document card if present
    url?: string;        // placeholder until the document viewer/download endpoint is wired up
}

export interface CaseEvent {
    date: string;   // e.g. '14 Aug 2026'
    label: string;  // e.g. 'Land Registered — Chennai'
}

// An opposing party as entered by the client. Kept optional/partial since
// the real API only ever returns bare name strings today, but the Overview
// UI already supports the richer shape below for any future/legacy record.
export interface OpposingParty {
    name: string;
    phone?: string;
    role?: string;    // Role / Relationship
    email?: string;
    address?: string;
}

export type CaseStatus = 'completed' | 'in_progress' | 'draft';

export interface CaseInformation {
    caseId: string;
    // The id the real "Case Information" GET API expects (professional/v3/prereads/shared/{pre_read_id}).
    // Always populated by mapSharedPreReadToCaseInformation(); kept optional purely
    // so the type also fits any legacy caller that only has a caseId.
    preReadId?: string;
    relationshipId: string;
    sharedByName?: string;
    issueTitle: string;
    issueType: string[];
    status: CaseStatus;
    sharedOn: string;
    updatedAt: string;
    // Legacy records may still supply plain name strings — the Overview UI
    // handles both shapes (see CaseDetailComponent.getPartyName/etc.).
    opposingParties?: (string | OpposingParty)[];
    incidentDescription?: string;
    expectedOutcome?: string[];
    legalActionTaken?: string;
    events?: CaseEvent[];
    deadline?: string;
    additionalInformation?: string;
    supportingDocuments?: SupportingDocument[];
    // Authoritative counts from the API (documents_count / notes_count) so
    // tab labels never have to be derived by counting arrays on the client.
    documentsCount?: number;
    notesCount?: number;
    // Raw notes as returned by the API, used only to seed CaseNotesService's
    // initial state for this case — see CaseDetailComponent.initNotesFromCase().
    notes?: SharedPreReadNote[];
}

// ---------------------------------------------------------------------------
// Real "Case Information" GET API contract
// GET {{url}}/professional/v3/prereads/shared/{pre_read_id}
// ---------------------------------------------------------------------------

export interface SharedPreReadKeyEvent {
    date: string;      // ISO date, e.g. '2026-08-14'
    event: string;      // e.g. 'Land Registered'
    location?: string;  // e.g. 'Chennai'
}

// `issue_type` / `expected_outcome` / `legal_action_taken` have been
// observed coming back from the API in two different shapes: a plain
// string array, and `{ selected: string[] }` (the same shape the client's
// multi-select form field posts). Typed as `SelectableList` so both are
// valid without `any`, and normalized to a flat array in
// mapSharedPreReadToCaseInformation() via toStringList() below — see the
// comment there for why this matters.
export type SelectableList = string[] | { selected?: string[] } | null;

export interface SharedPreReadOverview {
    case_title?: string;
    issue_type?: SelectableList;
    opposing_parties?: string[];
    incident_background?: string;
    expected_outcome?: SelectableList;
    legal_action_taken?: SelectableList;
    key_events?: SharedPreReadKeyEvent[];
    // Free-form key/value map so any deadline property the backend adds
    // later (not just `hearing_date`) renders without a code change.
    deadlines?: { [key: string]: string };
    additional_information?: string;
}

export interface SharedPreReadDocument {
    id: string;
    name: string;
    uploaded_on: string; // ISO date-time
}

// Shape of an individual note is not yet finalized on the backend (the
// sample response only ever shows an empty array). Kept loose/defensive on
// purpose — see CaseDetailComponent.mapApiNote().
export interface SharedPreReadNote {
    id?: string;
    content?: string;
    createdAt?: string;
    created_at?: string;
    author?: string;
    shared?: boolean;
    [key: string]: any;
}

export interface SharedPreReadData {
    case_title: string;
    status: string;
    shared_by: string;
    last_updated: string;
    overview: SharedPreReadOverview | null;
    documents: SharedPreReadDocument[] | null;
    documents_count: number;
    notes: SharedPreReadNote[] | null;
    notes_count: number;
    relationship_id: string;
}

export interface SharedPreReadResponse {
    error: boolean;
    data: SharedPreReadData | null;
}

// ---------------------------------------------------------------------------
// Listing API contract — GET {{url}}/v3/professional/prereads/shared
//
// Deliberately much lighter than SharedPreReadData above: this is what
// populates the Cases Shared list screen, and must NOT pull in each case's
// full detail (documents, notes, overview, etc.) just to render a card.
// ---------------------------------------------------------------------------

export interface SharedPreReadListItem {
    pre_read_id: string;
    case_title: string;
    // Same two shapes observed on the detail endpoint's issue_type — see
    // toStringList() below, reused here for identical handling.
    issue_type: SelectableList;
    status: string;
    shared_by?: string;
    shared_at: string;
    documents_count?: number;
    profile_pic?: string;
}

export interface SharedPreReadListData {
    total?: number;
    pre_reads: SharedPreReadListItem[] | null;
}

export interface SharedPreReadListResponse {
    error: boolean;
    data: SharedPreReadListData | null;
}

// View-model for a single Cases Shared card. Intentionally a separate,
// smaller shape from CaseInformation above — a list item only ever carries
// what the listing API actually returns.
export interface CaseListItem {
    preReadId: string;
    issueTitle: string;
    issueType: string[];
    sharedOn: string;
    status: CaseStatus;
    documentsCount?: number;
    // Per-case sharer's photo. The list screen only shows one avatar (in
    // the header, not per-card), so CaseListComponent picks whichever
    // item's photo to use — see its loadCases().
    profilePic?: string;
}

// ---------------------------------------------------------------------------
// Value formatter — turns backend enum-style tokens (snake_case, lowercase)
// into a professional display label while leaving the raw API value
// untouched wherever it is stored. Works for any future token, not just the
// ones currently returned by the API.
//   humanizeToken('obtain_an_injunction') -> 'Obtain an Injunction'
//   humanizeToken('fir_filed')            -> 'FIR Filed'
// ---------------------------------------------------------------------------
const KNOWN_ACRONYMS = new Set(['fir', 'pan', 'otp', 'id', 'ssn']);
const MINOR_WORDS = new Set(['a', 'an', 'the', 'of', 'in', 'on', 'and', 'or', 'for', 'to', 'with', 'at', 'by']);

export function humanizeToken(value?: string): string {
    if (!value) { return ''; }
    return value
        .split('_')
        .filter(word => word.length > 0)
        .map((word, index) => {
            const lower = word.toLowerCase();
            if (KNOWN_ACRONYMS.has(lower)) { return lower.toUpperCase(); }
            if (index > 0 && MINOR_WORDS.has(lower)) { return lower; }
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ');
}

// Normalizes `issue_type` / `expected_outcome` / `legal_action_taken` to a
// flat string array regardless of which shape the API sent this time.
// These fields have been observed both as a plain array (`["consumer"]`)
// and as `{ selected: ["consumer"] }` — calling `.filter()` straight on the
// latter throws (objects don't have `.filter`), which previously bubbled
// up as a thrown error out of mapSharedPreReadToCaseInformation(), got
// caught by the list screen's `catchError(() => of(null))`, and surfaced
// to the user as "Some cases could not be loaded." even though the API
// call itself had succeeded.
function toStringList(value?: SelectableList): string[] {
    if (!value) { return []; }
    if (Array.isArray(value)) { return value.filter(Boolean); }
    if (Array.isArray(value.selected)) { return value.selected.filter(Boolean); }
    return [];
}

/**
 * Cases Shared / Case Information — data service (Lawyer app / Relationships
 * / Individual only).
 *
 * `getSharedCaseInformation` is the one real, wired-up endpoint — it backs
 * both the Cases Shared list (one call per preReadId, see CaseListComponent)
 * and the Case Information (detail) screen. `mapSharedPreReadToCaseInformation`
 * is the single place that translates the raw API response into the shape
 * the existing (already-built) UI expects, so the raw response itself is
 * never mutated.
 *
 * NOTE: there is no "Case Summary" concept in this feature — Overview only
 * ever renders fields that were actually answered by the client.
 */
@Injectable({ providedIn: 'root' })
export class CaseInformationService {

    // DatePipe has no injected dependencies beyond an optional locale, so it
    // can be used directly here without adding a module-level provider.
    private readonly datePipe = new DatePipe('en-US');

    constructor(private httpservice: HttpService) { }

    /**
     * REAL API — Case Information (detail) screen + Cases Shared list.
     * GET {{url}}/professional/v3/prereads/shared/{pre_read_id}
     *
     * Surfaces `{ error: true }` API-level failures as a thrown error so
     * callers can rely on the Observable's `error` channel uniformly,
     * whether the failure is HTTP-level or API-level.
     */
    getSharedCaseInformation(preReadId: string): Observable<SharedPreReadData> {
        // NOTE: HttpService.getFeaturesdata() is not generic (it returns
        // whatever httpClient.get(...) infers based on the runtime-typed
        // `responseType` option), so the response has to be cast here
        // rather than requested via a type argument.
        return this.httpservice.getFeaturesdata(URLUtils.getSharedPreRead(preReadId)).pipe(
            map((response: unknown) => {
                const typedResponse = response as SharedPreReadResponse;
                if (!typedResponse || typedResponse.error || !typedResponse.data) {
                    throw new Error('Failed to load case information.');
                }
                return typedResponse.data;
            })
        );
    }

    /**
     * REAL API — Cases Shared listing screen ONLY.
     * GET {{url}}/professional/v3/prereads/shared
     *
     * Lightweight by design: returns just enough per case to render a
     * list card (title/issue-type/shared-on). It must never be used to
     * populate the Case Information (detail) screen — that stays on
     * getSharedCaseInformation() above, called only once the person
     * actually opens a case.
     *
     * NOTE: the endpoint needs to be scoped to the client/relationship
     * being viewed. The spec for this endpoint didn't include a sample
     * request, so this assumes a `relationship_id` query param (matching
     * the field name already used in SharedPreReadData.relationship_id
     * elsewhere in this file) — adjust the param name here if the actual
     * backend contract differs.
     */
    getSharedCaseList(relationshipId: string): Observable<CaseListItem[]> {
        return this.httpservice.getFeaturesdata(
            URLUtils.getSharedPreReadsList(),
            { relationship_id: relationshipId }
        ).pipe(
            map((response: unknown) => {
                const typedResponse = response as SharedPreReadListResponse;
                // FIX: the real response nests the list under "data"
                // ({ error, data: { total, pre_reads } }), not a bare
                // "pre_reads" at the response root. Reading
                // typedResponse.pre_reads directly (the previous code) was
                // always undefined, so the list rendered as empty even
                // though the API call succeeded and returned real data.
                if (!typedResponse || typedResponse.error || !typedResponse.data) {
                    throw new Error('Failed to load cases shared.');
                }
                return (typedResponse.data.pre_reads || [])
                    .filter(item => !!item)
                    .map(item => this.mapSharedPreReadListItem(item));
            })
        );
    }

    private mapSharedPreReadListItem(item: SharedPreReadListItem): CaseListItem {
        return {
            preReadId: item.pre_read_id,
            issueTitle: item.case_title || 'Untitled Legal Information',
            // Same normalizer already used for the detail endpoint's
            // issue_type — handles both the plain-array and
            // { selected: [...] } shapes this field has been observed in.
            issueType: toStringList(item.issue_type).map(humanizeToken),
            sharedOn: item.shared_at,
            status: this.normalizeStatus(item.status),
            documentsCount: item.documents_count,
            profilePic: item.profile_pic || undefined
        };
    }

    /**
     * Maps a raw SharedPreReadData API response onto the existing
     * CaseInformation shape the Overview/Documents/Notes UI already renders.
     * Every field is defensively read — missing/null/empty values simply
     * produce an empty/undefined result rather than throwing, so the
     * existing `hasValue()` checks in the template correctly hide the
     * corresponding card instead of rendering it blank.
     */
    mapSharedPreReadToCaseInformation(preReadId: string, data: SharedPreReadData): CaseInformation {
        const overview = data?.overview || ({} as SharedPreReadOverview);

        const keyEvents: CaseEvent[] = (overview.key_events || [])
            .filter(e => !!e?.event || !!e?.date)
            .slice()
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map(e => this.mapKeyEvent(e));

        const documents: SupportingDocument[] = (data?.documents || [])
            .filter(d => !!d)
            .map(d => this.mapDocument(d));

        return {
            caseId: preReadId,
            preReadId,
            relationshipId: data?.relationship_id || '',
            sharedByName: data?.shared_by || '',
            // Falls back to a friendly placeholder rather than an empty
            // heading when the client shared a case without ever titling it.
            issueTitle: data?.case_title || overview.case_title || 'Untitled Legal Information',
            issueType: toStringList(overview.issue_type).map(humanizeToken),
            status: this.normalizeStatus(data?.status),
            sharedOn: this.formatDate(data?.last_updated),
            updatedAt: this.formatDateTime(data?.last_updated),
            opposingParties: (overview.opposing_parties || []).filter(Boolean),
            incidentDescription: overview.incident_background || '',
            expectedOutcome: toStringList(overview.expected_outcome).map(humanizeToken),
            legalActionTaken: toStringList(overview.legal_action_taken).map(humanizeToken).join(', '),
            events: keyEvents,
            deadline: this.formatDeadlines(overview.deadlines),
            additionalInformation: overview.additional_information || '',
            supportingDocuments: documents,
            documentsCount: data?.documents_count ?? documents.length,
            notesCount: data?.notes_count ?? (data?.notes?.length || 0),
            notes: data?.notes || []
        };
    }

    private normalizeStatus(status?: string): CaseStatus {
        if (status === 'completed' || status === 'in_progress' || status === 'draft') {
            return status;
        }
        // Unrecognized future status values still flow through untouched for
        // business logic — CaseDetailComponent.statusLabel() falls back to a
        // humanized version of the raw value for display.
        return (status as CaseStatus) || 'draft';
    }

    private mapKeyEvent(e: SharedPreReadKeyEvent): CaseEvent {
        const label = e.location ? `${e.event} — ${e.location}` : (e.event || '');
        return { date: this.formatDate(e.date), label };
    }

    private mapDocument(doc: SharedPreReadDocument): SupportingDocument {
        const ext = (doc.name || '').split('.').pop() || '';
        return {
            id: doc.id,
            name: doc.name,
            fileType: ext.toUpperCase(),
            uploadedOn: this.formatDate(doc.uploaded_on)
        };
    }

    private formatDeadlines(deadlines?: { [key: string]: string } | null): string {
        if (!deadlines) { return ''; }
        const entries = Object.entries(deadlines).filter(([, v]) => !!v);
        if (!entries.length) { return ''; }
        return entries
            .map(([key, value]) => `${humanizeToken(key)}: ${this.formatDate(value)}`)
            .join('; ');
    }

    private formatDate(value?: string): string {
        if (!value) { return ''; }
        const transformed = this.safeTransform(value, 'd MMM y');
        return transformed || value;
    }

    private formatDateTime(value?: string): string {
        if (!value) { return ''; }
        const transformed = this.safeTransform(value, 'MMM d, y, h:mm a');
        return transformed || value;
    }

    private safeTransform(value: string, format: string): string | null {
        try {
            return this.datePipe.transform(value, format);
        } catch {
            return null;
        }
    }
}